import { useGraphQLQuery } from '@betterment/graphql';
import { Box, FormikField as Field, RadioGroup, Spinner, Text, VisuallyHidden } from '@betterment/samba-react';
import { ThemedImage } from '@betterment/shared-components/src/components/themed-image/ThemedImage';
import { useFormikContext } from 'formik';

import { ImageUploader } from './components/ImageUploader';
import { PurposeAssetsDocument } from './graphql/purposeIcons.generated';

type PurposeIconSelectorProps = {
  isCrypto?: boolean;
  customCoverImageAllowed?: boolean;
};

export const PurposeIconSelector: React.FC<React.PropsWithChildren<PurposeIconSelectorProps>> = ({
  isCrypto = false,
  customCoverImageAllowed = true,
}) => {
  const { values } = useFormikContext<{
    imageType: string;
    customImageData: string;
  }>();
  const isSelected = (imageName: string) => imageName === values.imageType;
  const { data } = useGraphQLQuery(PurposeAssetsDocument);

  if (!data)
    return (
      <Box css={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spinner />
      </Box>
    );
  if (!data.purposeAssets.length) return null;

  return (
    <Field name="imageType">
      <VisuallyHidden>
        <Text id="goal-image-selector">Select {isCrypto ? 'an account' : 'a goal'} image</Text>
      </VisuallyHidden>
      <Field.Control>
        <RadioGroup
          css={{
            display: 'grid',
            gridTemplateColumns: {
              base: '1fr 1fr 1fr 1fr',
              sm: '1fr 1fr 1fr 1fr 1fr',
              md: '1fr 1fr 1fr 1fr 1fr',
              lg: '1fr 1fr 1fr 1fr 1fr 1fr',
            },
          }}
        >
          {customCoverImageAllowed && (
            <RadioGroup.ItemContainer>
              <Box
                css={{
                  padding: 'space.100',
                  border: isSelected('custom')
                    ? 'token(borderWidths.thick) solid token(colors.border.selected)'
                    : 'token(borderWidths.thick) solid transparent',
                  borderRadius: 'large',
                }}
              >
                <ImageUploader />
              </Box>
            </RadioGroup.ItemContainer>
          )}
          {data.purposeAssets.map(asset => (
            <RadioGroup.ItemContainer key={asset.legacyImageKey}>
              <Box
                css={{
                  padding: 'space.100',
                  border: isSelected(asset.legacyImageKey)
                    ? 'token(borderWidths.thick) solid token(colors.border.selected)'
                    : 'token(borderWidths.thick) solid transparent',
                  borderRadius: 'large',
                }}
              >
                <RadioGroup.ItemLabel
                  htmlFor={`purpose-image-${asset.legacyImageKey}`}
                  css={{ display: 'flex', cursor: 'pointer' }}
                >
                  <ThemedImage alt={asset.legacyImageKey} themedAsset={asset.icon} css={{ width: 'size.600' }} />
                </RadioGroup.ItemLabel>

                <VisuallyHidden>
                  <RadioGroup.Item
                    id={`purpose-image-${asset.legacyImageKey}`}
                    value={asset.legacyImageKey}
                    aria-label={asset.legacyImageKey}
                  >
                    <Text>{asset.legacyImageKey}</Text>
                  </RadioGroup.Item>
                </VisuallyHidden>
              </Box>
            </RadioGroup.ItemContainer>
          ))}
        </RadioGroup>
      </Field.Control>
    </Field>
  );
};
